import { InventoryItem, Organization, QRCodeData } from '../types/models';
import { QRCodeUtils } from './qrCode';

export async function generatePrintReport(
  items: InventoryItem[],
  organization?: Organization | null
): Promise<void> {
  // Build report data
  const reportData = items.map(item => ({
    itemName: item.itemName,
    itemID: item.itemID,
    group: item.itemGroup || 'No Group',
    status: item.checkedOut ? 'Checked Out' : 'Available',
    checkedOutTo: item.checkedOutTo || 'N/A',
    checkedOutDate: item.checkedOutDate
      ? new Date(item.checkedOutDate * 1000).toLocaleDateString()
      : 'N/A',
  }));

  // Generate QR codes for all items
  const qrCodes = await Promise.all(
    items.map(async (item) => {
      const qrData: QRCodeData = {
        itemID: item.itemID,
        itemName: item.itemName,
        organizationID: organization?.id,
      };
      const dataUrl = await QRCodeUtils.generatePrintableQRCode(qrData);
      return {
        itemName: item.itemName,
        itemID: item.itemID,
        dataUrl,
      };
    })
  );

  // Create QR code pages (6 per page)
  const qrCodesPerPage = 6;
  const pages: string[] = [];

  for (let i = 0; i < qrCodes.length; i += qrCodesPerPage) {
    const pageQRCodes = qrCodes.slice(i, i + qrCodesPerPage);
    pages.push(`
      <div class="qr-page">
        <h2 style="page-break-before: always; text-align: center; margin-bottom: 30px;">
          QR Codes - Page ${Math.floor(i / qrCodesPerPage) + 1}
        </h2>
        <div class="qr-grid">
          ${pageQRCodes.map(qr => `
            <div class="qr-item">
              <img src="${qr.dataUrl}" alt="QR Code for ${qr.itemName}" />
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  const totalItems = items.length;
  const available = items.filter(item => !item.checkedOut).length;
  const checkedOut = items.filter(item => item.checkedOut).length;

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Inventory Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
          h1 { color: #333; text-align: center; margin-bottom: 30px; }
          h2 { color: #333; text-align: center; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; margin-bottom: 40px; }
          th, td { padding: 8px; text-align: left; border: 1px solid #ddd; font-size: 12px; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .available { color: #4caf50; }
          .checked-out { color: #f44336; }
          .summary {
            margin: 20px 0; padding: 15px; background-color: #f5f5f5;
            border-radius: 4px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px;
          }
          .summary p { margin: 0; font-weight: bold; }
          .qr-page { page-break-before: always; margin-top: 40px; }
          .qr-page:first-child { page-break-before: auto; }
          .qr-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 30px; margin-top: 30px; }
          .qr-item {
            text-align: center; page-break-inside: avoid; background: white;
            padding: 15px; border: 1px solid #ddd; border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .qr-item img { width: 280px; height: auto; display: block; margin: 0 auto; }
          @media print {
            body { margin: 0; }
            .qr-page { page-break-before: always; margin: 0; padding: 20px; }
            .qr-page:first-child { page-break-before: auto; }
            .qr-item { page-break-inside: avoid; break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Inventory Report</h1>
        <div class="summary">
          <p>Generated: ${new Date().toLocaleString()}</p>
          <p>Total Items: ${totalItems}</p>
          <p>Available: ${available}</p>
          <p>Checked Out: ${checkedOut}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Item ID</th>
              <th>Group</th>
              <th>Status</th>
              <th>Checked Out To</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.map(item => `
              <tr>
                <td>${item.itemName}</td>
                <td>${item.itemID}</td>
                <td>${item.group}</td>
                <td class="${item.status === 'Available' ? 'available' : 'checked-out'}">${item.status}</td>
                <td>${item.checkedOutTo}</td>
                <td>${item.checkedOutDate}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${pages.join('')}
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }
}
